import { useEffect, useState } from 'react';
import type { OptionType } from '../../types/forms.type';
import { FrontendGeoJsonFeature, MapClient } from '../../types/global';
import useDebounce from '../../hooks/useDebounce';

import PostWardMap from '../../services/adapters/mapWard';
import { DebtDimensionRequest } from '../../types/debt.type';
import BaseMap from '../map';
import { MapType } from '../../types/enums';

interface WardMapProps {
  mapConfig: MapClient;
  dropdownOptions: OptionType[];
  dimensions: DebtDimensionRequest;
}

export default function DebtMap({ mapConfig, dropdownOptions, dimensions }: Readonly<WardMapProps>) {
  const [wardData, setWardData] = useState<FrontendGeoJsonFeature[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const timeDelay = 500;
  const debouncedFilters = useDebounce(dropdownOptions, timeDelay);

  useEffect(() => {
    const abortController = new AbortController();
    const getWardData = async () => {
      setErrorMessage(null);
      setLoading(true);
      try {
        const mapAdapter = new PostWardMap();
        const response: FrontendGeoJsonFeature[] = await mapAdapter.adaptResponse(
          {
            data: {
              snapshot: dimensions.snapshot,
              liftDataId: dimensions.liftDataId,
              wardName: dimensions.wardName,
              metricName: dimensions.metricName,
              debtType: dimensions.debtType,
            },
            signal: abortController.signal,
          },
        );

        if (!abortController.signal.aborted) {
          setWardData(response);
          setLoading(false);
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setLoading(false);
          setErrorMessage((error as Error).message || 'An error occurred while fetching data.');
        }
      }
    };

    void getWardData();

    return () => {
      abortController.abort();
    };
  }, [debouncedFilters, dimensions.snapshot, dimensions.liftDataId, dimensions.wardName, dimensions.metricName, dimensions.debtType]);

  return (
    <div id="debt-map-container" className="relative h-full min-h-[300px] flex flex-col items-center">
      <BaseMap
        id="debt-map"
        mapConfig={mapConfig}
        mapType={MapType.WardMap}
        areaData={wardData}
        householdGroups={null}
      />
      {loading && !errorMessage && (
      <div className="absolute top-3 right-3 rounded bg-black/70 px-3 py-1 text-white">
        Updating map...
      </div>
      )}
      {
        errorMessage && (
          <div className="absolute top-3 right-3 rounded bg-red-600/90 px-3 py-1 text-white">
            {errorMessage}
          </div>
        )
      }
    </div>
  );
}
